export type ProductGroup = 'SS_TANKS' | 'DOMESTIC_HP' | 'COMMERCIAL_HP' | 'SS_PLUMBING';
export type PeriodType = 'MONTHLY' | 'QUARTERLY';

export interface SalesManagerTarget {
  id: string;
  userId: string;
  fiscalYear: string;
  periodType: PeriodType;
  periodIndex: number;
  productGroup: ProductGroup;
  targetLitres: number | null;
  targetRupees: number | null;
  overTargetRatePerLitre: number | null;
  overTargetPct: number | null;
  baseBonusInr: number;
}

export interface Manager {
  id: string;
  name: string;
}

export interface TargetsResponse {
  targets: SalesManagerTarget[];
  managers: Manager[];
}

export interface TargetPreview {
  overallAttainmentPct: number | null;
  totalBonusInr: number | null;
  perTarget?: Array<{
    productGroup: string;
    attainmentPct: number | null;
    bonusInr: number | null;
  }>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const PRODUCT_GROUPS: ProductGroup[] = [
  'SS_TANKS',
  'DOMESTIC_HP',
  'COMMERCIAL_HP',
  'SS_PLUMBING',
];

export const PRODUCT_GROUP_LABELS: Record<ProductGroup, string> = {
  SS_TANKS: 'SS Tanks',
  DOMESTIC_HP: 'Domestic Heat Pump',
  COMMERCIAL_HP: 'Commercial Heat Pump',
  SS_PLUMBING: 'SS Plumbing',
};

// Sun Stellar brand tokens
export const SAFFRON = '#D97706';   // amber-600
export const FOREST  = '#166534';   // green-800

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Format a number as Indian Rupees */
export function fmtINR(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

/** Format litres in Indian style */
export function fmtLitres(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M L`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K L`;
  return `${Math.round(n).toLocaleString('en-IN')} L`;
}

/**
 * Given a fiscal year string like "2026-27" and a periodType + periodIndex,
 * return [periodStart, periodEnd] as ISO date strings.
 * Indian FY starts April 1. Monthly: Apr=1 … Mar=12. Quarterly: Q1=Apr-Jun=1.
 */
export function periodDates(
  fiscalYear: string,
  periodType: PeriodType,
  periodIndex: number
): [string, string] {
  // Parse "YYYY-YY" → starting calendar year
  const parts = fiscalYear.split('-');
  const startYear = parseInt(parts[0] ?? '2026', 10);

  if (periodType === 'MONTHLY') {
    // Month index: 1=Apr(3), 2=May(4), …, 9=Dec(11), 10=Jan(0), 11=Feb(1), 12=Mar(2)
    const idx = ((periodIndex - 1 + 3) % 12); // 0-based JS month
    const year = periodIndex <= 9 ? startYear : startYear + 1;
    const start = new Date(Date.UTC(year, idx, 1));
    const end = new Date(Date.UTC(year, idx + 1, 0, 23, 59, 59, 999)); // last day of month
    return [start.toISOString(), end.toISOString()];
  } else {
    // QUARTERLY: Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar
    const quarterMonths: [number, number][] = [
      [3, 5],   // Q1: Apr(3) – Jun(5)
      [6, 8],   // Q2: Jul(6) – Sep(8)
      [9, 11],  // Q3: Oct(9) – Dec(11)
      [0, 2],   // Q4: Jan(0) – Mar(2)
    ];
    const [startMonth, endMonth] = quarterMonths[(periodIndex - 1) % 4] ?? [3, 5];
    const year = periodIndex === 4 ? startYear + 1 : startYear;
    const start = new Date(Date.UTC(year, startMonth, 1));
    const end = new Date(Date.UTC(year, endMonth + 1, 0, 23, 59, 59, 999));
    return [start.toISOString(), end.toISOString()];
  }
}

// ─── Row state types ──────────────────────────────────────────────────────────

export interface RowDraft {
  targetLitres: string;
  targetRupees: string;
  overTargetRatePerLitre: string;
  overTargetPct: string;
  baseBonusInr: string;
}

export interface RowStatus {
  saving: boolean;
  saved: boolean;
  error: string | null;
  deleting: boolean;
}

export function emptyDraft(): RowDraft {
  return {
    targetLitres: '',
    targetRupees: '',
    overTargetRatePerLitre: '',
    overTargetPct: '',
    baseBonusInr: '0',
  };
}

export function targetToDraft(t: SalesManagerTarget): RowDraft {
  return {
    targetLitres: t.targetLitres != null ? String(t.targetLitres) : '',
    targetRupees: t.targetRupees != null ? String(t.targetRupees) : '',
    overTargetRatePerLitre: t.overTargetRatePerLitre != null ? String(t.overTargetRatePerLitre) : '',
    overTargetPct: t.overTargetPct != null ? String(t.overTargetPct) : '',
    baseBonusInr: String(t.baseBonusInr),
  };
}

export function parseOptionalPositive(s: string): number | null {
  if (!s.trim()) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function validateDraft(draft: RowDraft): string | null {
  const fields: Array<[string, string]> = [
    [draft.targetLitres, 'Target Litres'],
    [draft.targetRupees, 'Target ₹'],
    [draft.overTargetRatePerLitre, 'Over-target ₹/L'],
    [draft.overTargetPct, 'Over-target %'],
  ];
  for (const [val, label] of fields) {
    if (val.trim() === '') continue;
    const n = parseFloat(val);
    if (!Number.isFinite(n) || n < 0) return `${label} must be ≥ 0`;
  }
  const bonus = parseFloat(draft.baseBonusInr);
  if (!Number.isFinite(bonus) || bonus < 0) return 'Base Bonus must be ≥ 0';
  return null;
}

